import dayjs from 'dayjs';
import type { BarDuration } from '@einhaus/project314-types';
import type { UTCTimestamp } from 'lightweight-charts';
import { getDate, getDatePlusMinus, getUnixTime } from '@einhaus/meteoric-helpers';

export const convertEstTimestampToTradingView = (estTimestamp: number): UTCTimestamp => {
    // Get rid of trailing seconds, since they can sometimes trip things up and make Trading view look at the next bar
    estTimestamp = Math.floor(estTimestamp / 10) * 10;
    const OFFSET_DIFFERENCE_IN_SECONDS = -300 * 60;

    const DST_OFFSET = isTimestampDST(estTimestamp) ? 60 * 60 : 0;

    const convertedTimestamp = estTimestamp + OFFSET_DIFFERENCE_IN_SECONDS + DST_OFFSET;

    return convertedTimestamp as UTCTimestamp;
};

export const convertTradingViewTimestampToEst = (tradingViewTimestamp: UTCTimestamp): number => {
    const OFFSET_DIFFERENCE_IN_SECONDS = -300 * 60;
    const DST_OFFSET = isTimestampDST(tradingViewTimestamp) ? 60 * 60 : 0;
    const convertedTimestamp = tradingViewTimestamp - OFFSET_DIFFERENCE_IN_SECONDS - DST_OFFSET;
    return convertedTimestamp;
};

export const isTimestampDST = (timestamp: number) => {
    const d = new Date(timestamp * 1000);
    const jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset();
    const jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset();
    return Math.max(jan, jul) !== d.getTimezoneOffset();
};

export const getLookbackDateForDuration = (duration: BarDuration) => {
    let lookbackDate =
        duration === '1d' ? getDatePlusMinus('-', 5, 'year', 'ymdhms', 'start') : getDatePlusMinus('-', 30, 'day', 'ymdhms', 'start');

    lookbackDate = duration === '1w' ? getDatePlusMinus('-', 5, 'year', 'ymdhms', 'start') : lookbackDate;
    lookbackDate = duration === '1mo' ? getDatePlusMinus('-', 10, 'year', 'ymdhms', 'start') : lookbackDate;

    return lookbackDate;
};

export const createMonthRangesUsingTimestamps = (timestamps: number[]) => {
    const months: { start: number; end: number }[] = [];

    for (const timestamp of timestamps) {
        const startOfMonth = getDate({
            date: timestamp,
            startOf: 'month',
            format: 'unix'
        });

        const findMonth = months.find((month) => month.start === +startOfMonth);
        if (findMonth) continue;

        const sendOfMonth = getDate({
            date: timestamp,
            endOf: 'month',
            format: 'unix'
        });

        months.push({ start: +startOfMonth, end: +sendOfMonth });
    }

    return months;
};

export const getQuarterRangesUsingTimestamps = (timestamps: number[]) => {
    const minTimestamp = Math.min(...timestamps);
    const maxTimestamp = Math.max(...timestamps);

    const minYear = dayjs.unix(minTimestamp).year();
    const maxYear = dayjs.unix(maxTimestamp).year();

    const quarters = [
        { startMonth: '1', endMonth: '3' },
        { startMonth: '4', endMonth: '6' },
        { startMonth: '7', endMonth: '9' },
        { startMonth: '10', endMonth: '12' }
    ];

    const returnQuarters: { start: number; end: number }[] = [];

    let didReachEndQuarter = false;

    for (let year = minYear; year <= maxYear; year++) {
        for (const quarter of quarters) {
            const start = getUnixTime(`${year}-${quarter.startMonth}-01`, 'start');
            const end = getUnixTime(`${year}-${quarter.endMonth}-31`, 'end');

            if (dayjs(end).unix() > maxTimestamp) {
                didReachEndQuarter = true;
                returnQuarters.push({ start: +start, end: maxTimestamp });
                break;
            }

            returnQuarters.push({ start: +start, end: +end });
        }

        if (didReachEndQuarter) break;
    }

    return returnQuarters;
};
